// Types for F-tag and Inspection API responses

export type SeverityLevel = 'MINOR' | 'MODERATE' | 'HARM' | 'JEOPARDY';
export type InspectionType = 'HEALTH' | 'COMPLAINT' | 'COMBINED';

export interface FTagDefinition {
  ftag: string;
  name: string;
  description: string;
  cfr_citation: string;
  category: string;
  is_understaffing_core: boolean;
  is_understaffing_indicator: boolean;
  understaffing_relevance: string;
}

export interface SNFInspectionCitation {
  id: string;
  ccn: string;
  survey_date: string;
  ftag: string;
  ftag_display: string;
  ftag_prefix: string;
  ftag_category: string;
  ftag_description: string;
  scope_severity_code: string;
  severity_level: SeverityLevel;
  severity_category: string;
  scope: string;
  harm_level: string;
  is_harm: boolean;
  is_staffing: boolean;
  is_abuse: boolean;
  is_understaffing_core: boolean;
  is_understaffing_indicator: boolean;
  was_corrected: boolean;
  correction_date: string | null;
  was_removed: boolean;
  inspection_source: 'health' | 'complaint' | 'both';
  inspection_source_display: string;
}

export interface SNFInspection {
  id: string;
  ccn: string;
  state: string;
  inspection_date: string;
  inspection_type: InspectionType;
  inspection_type_display: string;
  survey_cycle: number | null;
  total_citations: number;
  minor_citations: number;
  moderate_citations: number;
  harm_citations: number;
  jeopardy_citations: number;
  staffing_citations: number;
  has_staffing_citation: boolean;
  has_abuse_citation: boolean;
  has_harm_or_jeopardy: boolean;
  has_report: boolean;
  report_url: string | null;
  worst_severity: SeverityLevel | null;
  citations?: SNFInspectionCitation[];
}

export interface SNFFTagProfile {
  id: string;
  ccn: string;
  state: string;
  total_inspections: number;
  health_inspections: number;
  complaint_inspections: number;
  ftag_counts: Record<string, number>;
  ftag_harm_counts: Record<string, number>;
  category_counts: Record<string, number>;
  category_per_100: Record<string, number>;
  category_harm_counts: Record<string, number>;
  total_citations: number;
  total_citations_per_100: number | null;
  minor_citations: number;
  moderate_citations: number;
  harm_citations: number;
  total_harm_citations: number;
  jeopardy_citations: number;
  total_jeopardy_citations: number;
  citations_per_100: number | null;
  harm_per_100: number | null;
  understaffing_abuse_indicator_score: number | null;
  understaffing_core_total: number;
  understaffing_indicator_total: number;
  state_percentiles: {
    citations: number;
    harm: number;
    staffing: number;
    abuse: number;
    abuse_indicator?: number;
  };
  state_averages: Record<string, number>;
}

// Severity code to display info mapping
// CMS Scope & Severity Grid labels
// Severity: No Actual Harm (minimal) | Potential Harm (more than minimal) | Actual Harm | Immediate Jeopardy
// Scope: Isolated | Pattern | Widespread
export const SEVERITY_INFO: Record<string, { label: string; color: string; bgColor: string }> = {
  A: { label: 'A - Isolated, No Actual Harm', color: '#2e7d32', bgColor: '#e8f5e9' },
  B: { label: 'B - Pattern, No Actual Harm', color: '#2e7d32', bgColor: '#e8f5e9' },
  C: { label: 'C - Widespread, No Actual Harm', color: '#2e7d32', bgColor: '#e8f5e9' },
  D: { label: 'D - Isolated, Potential Harm', color: '#ed6c02', bgColor: '#fff3e0' },
  E: { label: 'E - Pattern, Potential Harm', color: '#ed6c02', bgColor: '#fff3e0' },
  F: { label: 'F - Widespread, Potential Harm', color: '#ed6c02', bgColor: '#fff3e0' },
  G: { label: 'G - Isolated, Actual Harm', color: '#d32f2f', bgColor: '#ffebee' },
  H: { label: 'H - Pattern, Actual Harm', color: '#d32f2f', bgColor: '#ffebee' },
  I: { label: 'I - Widespread, Actual Harm', color: '#d32f2f', bgColor: '#ffebee' },
  J: { label: 'J - Isolated, Imm. Jeopardy', color: '#7b1fa2', bgColor: '#f3e5f5' },
  K: { label: 'K - Pattern, Imm. Jeopardy', color: '#7b1fa2', bgColor: '#f3e5f5' },
  L: { label: 'L - Widespread, Imm. Jeopardy', color: '#7b1fa2', bgColor: '#f3e5f5' },
};

export const getSeverityColor = (code: string): string => {
  return SEVERITY_INFO[code]?.color || '#757575';
};

export const getSeverityBgColor = (code: string): string => {
  return SEVERITY_INFO[code]?.bgColor || '#f5f5f5';
};

export const getSeverityLevelColor = (level: SeverityLevel | null): string => {
  switch (level) {
    case 'MINOR': return '#2e7d32';
    case 'MODERATE': return '#ed6c02';
    case 'HARM': return '#d32f2f';
    case 'JEOPARDY': return '#7b1fa2';
    default: return '#757575';
  }
};

// F-tag categories for grouping
export const FTAG_CATEGORIES = {
  staffing: ['F725', 'F726', 'F727', 'F728', 'F729', 'F730', 'F731', 'F732', 'F851'],
  abuse_neglect: ['F600', 'F602', 'F603', 'F604', 'F605', 'F606', 'F607', 'F608', 'F609', 'F610'],
  quality_of_care: ['F684', 'F685', 'F686', 'F687', 'F688', 'F689', 'F690', 'F691', 'F692', 'F693', 'F694', 'F695', 'F696', 'F697', 'F698', 'F699'],
  behavioral_health: ['F740', 'F741', 'F742', 'F743', 'F744', 'F745'],
  pharmacy: ['F755', 'F756', 'F757', 'F758', 'F759', 'F760', 'F761'],
  infection_control: ['F880', 'F881', 'F882', 'F883', 'F884', 'F885', 'F886'],
};

export const UNDERSTAFFING_CORE = ['F725', 'F726', 'F727', 'F851'];
export const UNDERSTAFFING_INDICATORS = ['F600', 'F684', 'F686', 'F689', 'F690', 'F692', 'F757', 'F758', 'F759', 'F880'];